import { CaseClosingConfigurationDto, CaseClosingConfigurationUpdateDto } from 'types/caseClosing';

export type AutomaticClosingSettings = Pick<
    CaseClosingConfigurationDto,
    'isAutomaticClosingEnabled' | 'automaticClosingInactivityDays'
>;

export enum AutomaticClosingConfirmation {
    SWITCH_ON = 'SWITCH_ON',
    CHANGE = 'CHANGE',
}

/**
 * Saving can make the next run close cases that the stored settings would leave open: switching automatic
 * closing on, or lowering its days while it is on. Only those two saves are confirmed with the run's count.
 */
export function automaticClosingConfirmation(
    saved: AutomaticClosingSettings,
    edited: AutomaticClosingSettings
): AutomaticClosingConfirmation | null {
    if (!edited.isAutomaticClosingEnabled) {
        return null;
    }
    if (!saved.isAutomaticClosingEnabled) {
        return AutomaticClosingConfirmation.SWITCH_ON;
    }
    return edited.automaticClosingInactivityDays < saved.automaticClosingInactivityDays
        ? AutomaticClosingConfirmation.CHANGE
        : null;
}

export const MAX_INACTIVITY_DAYS = 1000;

export function isValidInactivityDays(value: number | ''): value is number {
    return value !== '' && Number.isInteger(value) && value >= 1 && value <= MAX_INACTIVITY_DAYS;
}

export function inactivityDaysErrorMessageId(value: number | ''): string {
    if (value !== '' && !Number.isInteger(value)) {
        return 'case-closing-config.whole-days-error';
    }
    if (value !== '' && value > MAX_INACTIVITY_DAYS) {
        return 'case-closing-config.max-inactivity-days-error';
    }
    return 'configuration.min-days-error';
}

/** Each subsection saves the whole settings object, echoing the stored values of the ones it does not edit. */
export function toCaseClosingUpdate(
    saved: CaseClosingConfigurationDto,
    changes: Partial<CaseClosingConfigurationUpdateDto>
): CaseClosingConfigurationUpdateDto {
    return {
        requiresNoUnfinishedTasks: changes.requiresNoUnfinishedTasks ?? saved.requiresNoUnfinishedTasks,
        requiresNoUnsettledInvoices: changes.requiresNoUnsettledInvoices ?? saved.requiresNoUnsettledInvoices,
        requiresNoUnusedPrepayments: changes.requiresNoUnusedPrepayments ?? saved.requiresNoUnusedPrepayments,
        isAutomaticClosingEnabled: changes.isAutomaticClosingEnabled ?? saved.isAutomaticClosingEnabled,
        automaticClosingInactivityDays: changes.automaticClosingInactivityDays ?? saved.automaticClosingInactivityDays,
        isClosingOnInvoiceSendEnabled: changes.isClosingOnInvoiceSendEnabled ?? saved.isClosingOnInvoiceSendEnabled,
        isConfidentialReasonRequired: changes.isConfidentialReasonRequired ?? saved.isConfidentialReasonRequired,
    };
}
